/**
 * GlobalView — Toast Notification System
 *
 * Lightweight toast system using Zustand (§26.3).
 */

import { create } from 'zustand';
import styles from './Toast.module.css';

type ToastType = 'info' | 'success' | 'warning' | 'error';

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastStore {
  toasts: ToastItem[];
  addToast: (message: string, type?: ToastType, duration?: number) => void;
  removeToast: (id: string) => void;
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  addToast: (message, type = 'info', duration = 4000) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    set((state) => ({
      toasts: [...state.toasts, { id, message, type, duration }],
    }));
    if (duration > 0) {
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      }, duration);
    }
  },
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),
}));

const TYPE_ICON: Record<ToastType, string> = {
  info: 'ℹ️',
  success: '✅',
  warning: '⚠️',
  error: '❌',
};

const TYPE_COLOR: Record<ToastType, string> = {
  info: 'var(--accent)',
  success: 'var(--accent4)',
  warning: 'var(--accent5)',
  error: 'var(--accent3)',
};

export default function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);

  if (toasts.length === 0) return null;

  return (
    <div className={styles.container} id="toast-container">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={styles.toast}
          style={{ '--toast-color': TYPE_COLOR[toast.type] } as React.CSSProperties}
          onClick={() => removeToast(toast.id)}
        >
          <span className={styles.icon}>{TYPE_ICON[toast.type]}</span>
          <span className={styles.message}>{toast.message}</span>
        </div>
      ))}
    </div>
  );
}
